import React, { FC, useEffect, useState, useRef } from 'react';
import './DesktopShopThelookModal.scss';
import './BannerModal.scss';
import Col from 'react-bootstrap/Col';
import Slider from 'react-slick';
import Card from '../ShopTheLook/Card';
import usePrevious from '../../hooks/usePrevious';
import {
  gtmEventPush,
  gtmPagePush,
  luxeGtmProductImpressions
} from '../../../modules/ga/GTMdatalayer';
import { getProductIds, getBannerName, getProductsArray, sortProductList } from './utils';
import Video from '../../molecules/Video';
import { audioSettings, dynamicPageMetadata, videoSettings } from './types';
import { Feature, Variant } from '../ABFeature';
import { replaceWithJioCDN } from '../../../utils/imageHandling';

interface ShopthelookProps {
  isLuxe: boolean;
  allBanners: string[];
  currentBannerInRow: number;
  variant: string;
  loading: boolean;
  getProductsDetails: (ids: string, index: number) => void;
  productsData: Record<string, import('../../types/product').Product[]>;
  priceRevealMetaInfo: Record<string, number>;
  priceRevealState: boolean;
  currentStore: string;
  rowNumber: number;
  username: string;
  dynamicPageMetadata: dynamicPageMetadata;
  videoSettings: videoSettings;
  audioSettings: audioSettings;
  bannerType: string;
  updateBannerDetails: ({ currentIndex }: { currentIndex: number }) => void;
  updateVideoDetails: ({
    name,
    loadTime
  }: {
    name: string;
    loadTime: number;
    currentIndex: undefined | number;
  }) => void;
}

const Shopthelook: FC<ShopthelookProps> = (props: ShopthelookProps) => {
  let {
    isLuxe,
    allBanners = [],
    currentBannerInRow,
    variant,
    getProductsDetails,
    productsData,
    priceRevealMetaInfo,
    loading,
    priceRevealState,
    currentStore,
    rowNumber,
    username,
    updateBannerDetails,
    updateVideoDetails
  } = props;
  const [currentRowIndex, setCurrentRowIndex] = useState(currentBannerInRow);
  const [currentBannerMediaType, setCurrentBannerMediaType] = useState('VIDEO');
  const productKey = `${rowNumber}-${currentRowIndex}`;
  const products = sortProductList(productsData[productKey]);

  const [currentVideoDetails, setCurrentVideoDetails] = useState({
    videoName: '',
    video_load_time: 0,
    isAutoPlayEnabled: true
  });

  const scrollContainer = useRef();
  useEffect(() => {
    if (!products) {
      const productIds = getProductIds(allBanners[currentRowIndex]);
      getProductsDetails(productIds, productKey);
    }
  }, [currentRowIndex]);

  useEffect(() => {
    if (currentBannerMediaType === 'VIDEO') {
      gtmPagePush('shop the look video screen', 'shop the look video screen', '', {
        store: isLuxe ? 'luxe' : 'ajio'
      });
    }
  }, [currentBannerMediaType]);

  let slickSettings = {
    initialSlide: currentRowIndex,
    autoplay: false,
    infinite: false,
    arrows: true,
    dots: true,
    slidesToShow: 1,
    afterChange: (current: number, next: number) => {
      setCurrentRowIndex(current);
    }
  };

  const updateCurrentVideoDetails = ({
    name,
    loadTime,
    currentIndex
  }: {
    name: string;
    loadTime: number;
    currentIndex: number;
  }) => {
    setCurrentVideoDetails({
      videoName: name,
      video_load_time: loadTime,
      isAutoPlayEnabled:
        allBanners[currentIndex - 1]?.dynamicPageMetadata?.videoSettings?.autoPlay || true
    });
    updateVideoDetails({ name, loadTime, currentIndex });
  };

  useEffect(() => {
    if (products && currentBannerMediaType) {
      const ProductArray = getProductsArray(products, currentBannerMediaType);
      luxeGtmProductImpressions({
        impressions: ProductArray,
        page_name:
          currentBannerMediaType === 'IMAGE' ? 'shop the look' : 'shop the look video screen',
        page_type:
          currentBannerMediaType === 'IMAGE' ? 'shop the look' : 'shop the look video screen',
        store_type: isLuxe ? 'luxe' : 'ajio',
        fromVideoBanner: currentBannerMediaType !== 'IMAGE',
        video_name: currentVideoDetails.videoName || '',
        video_load_time: currentVideoDetails.video_load_time || 0,
        ProductArray
      });
    }
  }, [products, currentBannerMediaType]);

  useEffect(() => {
    updateBannerDetails({ currentIndex: currentRowIndex });
    const bannerType = allBanners[currentRowIndex]?.dynamicPageMetadata?.mediaType || 'IMAGE';
    setCurrentBannerMediaType(bannerType);
  }, [currentRowIndex]);

  const prevCurrentRowIndex: number = usePrevious(currentRowIndex);
  const bannerName = getBannerName(allBanners[currentRowIndex]);
  useEffect(() => {
    if (currentRowIndex !== prevCurrentRowIndex) {
      if (currentBannerMediaType === 'IMAGE') {
        gtmEventPush({
          event: 'shop_the_look',
          event_category: 'Shop the Look',
          event_action:
            currentRowIndex - prevCurrentRowIndex < 0 ? 'Banner Swipe Right' : 'Banner Swipe Left',
          event_label: bannerName
        });
      } else {
        const { videoName, isAutoPlayEnabled, video_load_time } = currentVideoDetails;
        gtmEventPush({
          event: 'video_screen_interactions',
          event_category: 'shop the look video interactions',
          event_action: currentRowIndex - prevCurrentRowIndex < 0 ? 'previous video' : 'next video',
          video_name: videoName,
          is_auto_play_enabled: isAutoPlayEnabled,
          video_load_time,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: 'shop the look video screen',
          page_type: 'shop the look video screen'
        });
      }
    }
  }, [prevCurrentRowIndex, currentRowIndex]);

  useEffect(() => {
    setTimeout(() => {
      setScrollPosition();
    }, 1000);
  }, []);

  const listenCurrentScrollPosition = () => {
    localStorage.setItem('shopTheLookYPos', scrollContainer.current.scrollTop);
  };

  const setScrollPosition = () => {
    let position = localStorage.getItem('shopTheLookYPos');
    if (scrollContainer && scrollContainer.current && position && parseInt(position) > 0) {
      scrollContainer.current.scrollTop = parseInt(position);
    }
  };
  const platform = isLuxe ? 'desktopLuxe' : 'desktop';

  const selectedBannerData = allBanners ? allBanners[currentRowIndex] : {};
  const { dynamicPageMetadata: { subTitle = '', title = ' ' } = {} } = selectedBannerData;
  return (
    <div className="model-container">
      <div className="header">
        {loading ? (
          <>
            <div className="stripe shm_title" />
            <div className="stripe shm_subtitle" />
          </>
        ) : (
          <>
            <div className="title">{title}</div>
            <div className="sub-title">{subTitle}</div>
          </>
        )}
      </div>
      <div className="banner-container">
        <Col className="img-container" md={5} id="desktop-banner-images">
          {loading ? (
            <div className="image-shm" />
          ) : (
            <Slider {...slickSettings}>
              {allBanners?.map((val, index) => {
                const {
                  dynamicPageMetadata: {
                    extendedImageUrl = '',
                    mediaType = 'IMAGE',
                    audioSettings: { muteOnFullScreen = true } = {},
                    videoSettings: { autoPlay = true, loop = true, showTimestamp = true } = {}
                  } = {},
                  thumbnailImage = ''
                } = val;
                return (
                  <div>
                    {mediaType === 'VIDEO' ? (
                      <Feature featureName="videoBannerShopTheLook">
                        <Variant id="0">{null}</Variant>
                        <Variant id="1">
                          <Video
                            autoPlay={autoPlay}
                            videoURL={extendedImageUrl}
                            thumbnailImage={thumbnailImage}
                            muted={muteOnFullScreen}
                            displayProgressBar
                            showPlayPauseHandler
                            isModal
                            onClickOpenModal={false}
                            displayTopGradient
                            showTimestamp={showTimestamp}
                            loop={loop}
                            isShopThelook
                            rowNumber={rowNumber}
                            currentIndex={currentRowIndex}
                            updateVideoDetails={updateCurrentVideoDetails}
                            deviceType="desktop"
                          />
                        </Variant>
                      </Feature>
                    ) : (
                      <img
                        key={index}
                        src={replaceWithJioCDN(extendedImageUrl)}
                        alt="banner-img"
                        className="banner-img"
                      />
                    )}
                  </div>
                );
              })}
            </Slider>
          )}
        </Col>
        <Col className="products" md={7} ref={scrollContainer}>
          {products
            ? products.map((product, key) => (
                <Card
                  product={product}
                  id={`shopTheLook ${key}`}
                  platform={platform}
                  variant="dekstop"
                  priceRevealMetaInfo={priceRevealMetaInfo}
                  priceRevealState={priceRevealState}
                  loading={loading}
                  bannerName={bannerName}
                  isLuxe={isLuxe}
                  currentStore={currentStore}
                  username={username}
                  rowNumber={rowNumber}
                  currentRowIndex={currentRowIndex}
                  currentBannerMediaType={currentBannerMediaType}
                  currentVideoDetails={currentVideoDetails}
                  getCurrentScrollPosition={listenCurrentScrollPosition}
                />
              ))
            : [1, 2, 3].map(key => (
                <Card variant="dekstop" loading={loading} id={`ShopTheLook${key}`} />
              ))}
        </Col>
      </div>
    </div>
  );
};
export default Shopthelook;
